// @givmo/design-system — DataTable (SaaS layer) — the workhorse.
//
// Two tiers:
//   1. PRIMITIVES — themed <Table>/<THead>/<TBody>/<TR>/<TH>/<TD> for hand-built tables. Headers
//      are text-tertiary uppercase-xs/medium; rows have a border-subtle divider + hover; numeric
//      cells right-align via `align="right"`; `TH` supports a `sort` affordance.
//   2. COMPOSED <DataTable> — a column-config-driven table that wires the primitives plus a sticky
//      toolbar slot (search/filters/primary action), sortable headers, hover rows, an empty/
//      loading/error state, and an optional pagination footer with counts. Generic over the row
//      type; columns declare an accessor + alignment + optional sort key.
//
// a11y: a real <table> with <thead>/<tbody>; sortable headers carry `aria-sort`; the toolbar is a
// region. `testId` forwards to the outer wrapper; rows accept a `rowTestId(row)` deriver.
//
// ROW DISCLOSURE (`renderExpanded`): a first-class expandable row, because the alternative a screen
// grew on its own — a native `title` tooltip — is invisible to keyboard and screen-reader users, which
// is not acceptable for a control that explains what a charge covers. The toggle is a real
// `<button aria-expanded aria-controls>` in a leading disclosure column, and the detail renders as its
// own `<tr id>` spanning every column, so the relationship is announced rather than implied.
import { memo, useCallback, useRef, useState } from 'react';
import type { HTMLAttributes, ReactNode, Ref, ThHTMLAttributes, TdHTMLAttributes } from 'react';
import { ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '../lib/utils';
import { FOCUS_RING } from '../lib/focus';
import { testIdProp, type TestIdProps } from '../lib/testid';
import { Icon } from './icon';
import { Skeleton } from './skeleton';
import { Button } from './button';

// ── Primitives ────────────────────────────────────────────────────────────────────────────────

export interface TableProps extends HTMLAttributes<HTMLTableElement>, TestIdProps {
  /** Classes for the scroll wrapper (the stacked mode drops its scrolling — see STACK_CLASS). */
  wrapperClassName?: string;
  /** Additive controls for the div that owns horizontal (and, when requested, vertical) scrolling. */
  wrapperTestId?: string;
  wrapperProps?: Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'className'>;
  wrapperRef?: Ref<HTMLDivElement>;
}
export function Table({
  className,
  testId,
  children,
  wrapperClassName,
  wrapperTestId,
  wrapperProps,
  wrapperRef,
  ...props
}: TableProps) {
  return (
    <div
      ref={wrapperRef}
      className={cn('w-full overflow-x-auto', wrapperClassName)}
      {...wrapperProps}
      {...testIdProp(wrapperTestId)}
    >
      <table
        className={cn('w-full border-collapse text-left', className)}
        {...testIdProp(testId)}
        {...props}
      >
        {children}
      </table>
    </div>
  );
}

export interface THeadProps extends HTMLAttributes<HTMLTableSectionElement> {
  /** Pin the header while the body scrolls (long lists). */
  sticky?: boolean;
}
export function THead({ className, sticky = false, children, ...props }: THeadProps) {
  return (
    <thead className={cn('bg-neutral-50', sticky && 'sticky top-0 z-10', className)} {...props}>
      {children}
    </thead>
  );
}

export function TBody({ className, children, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody className={className} {...props}>
      {children}
    </tbody>
  );
}

export interface TRProps extends HTMLAttributes<HTMLTableRowElement>, TestIdProps {
  /** Apply the interactive hover affordance (for clickable rows). */
  interactive?: boolean;
}
export function TR({ className, interactive = false, testId, children, ...props }: TRProps) {
  return (
    <tr
      className={cn(
        'border-b border-border-subtle last:border-b-0',
        interactive && 'cursor-pointer transition-colors hover:bg-surface-hover',
        className,
      )}
      {...testIdProp(testId)}
      {...props}
    >
      {children}
    </tr>
  );
}

export type CellAlign = 'left' | 'right' | 'center';

const ALIGN_CLASS: Record<CellAlign, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

export interface THProps extends ThHTMLAttributes<HTMLTableCellElement> {
  align?: CellAlign;
  /** Current sort state for this column ('asc' | 'desc' | false = sortable-but-inactive). */
  sort?: 'asc' | 'desc' | false;
  onSort?: () => void;
}
export function TH({ className, align = 'left', sort, onSort, children, ...props }: THProps) {
  const sortable = sort !== undefined;
  const ariaSort = sort === 'asc' ? 'ascending' : sort === 'desc' ? 'descending' : 'none';
  return (
    <th
      scope="col"
      aria-sort={sortable ? ariaSort : undefined}
      className={cn(
        'h-[40px] px-16 font-sans text-s11 font-w600 uppercase tracking-wide text-text-secondary',
        'whitespace-nowrap border-b border-border-subtle',
        ALIGN_CLASS[align],
        className,
      )}
      {...props}
    >
      {sortable ? (
        <button
          type="button"
          onClick={onSort}
          className={cn(
            'inline-flex items-center gap-4 uppercase tracking-wide outline-none',
            'transition-colors hover:text-text-strong focus-visible:text-text-strong',
            align === 'right' && 'flex-row-reverse',
          )}
        >
          {children}
          <Icon
            icon={sort === 'asc' ? ChevronUp : sort === 'desc' ? ChevronDown : ChevronsUpDown}
            size={13}
            // Idle chevron uses text-tertiary (icon-muted is near-invisible on the neutral-50 header).
            className={sort ? 'text-text-secondary' : 'text-text-tertiary'}
            aria-hidden
          />
        </button>
      ) : (
        children
      )}
    </th>
  );
}

export interface TDProps extends TdHTMLAttributes<HTMLTableCellElement> {
  align?: CellAlign;
  /** Render numerics with tabular figures (right-aligned columns usually want this). */
  numeric?: boolean;
}
export function TD({ className, align = 'left', numeric = false, children, ...props }: TDProps) {
  return (
    <td
      className={cn(
        'h-[52px] px-16 font-sans text-s14 font-w400 tracking-givmo text-text',
        ALIGN_CLASS[align],
        numeric && 'tabular-nums',
        className,
      )}
      {...props}
    >
      {children}
    </td>
  );
}

// ── Stacked rows below a breakpoint (`stackAt`, OM-13) ─────────────────────────────────────────
//
// ══ THE FAILURE THIS EXISTS TO CURE ════════════════════════════════════════════════════════════
//
// A table in an `overflow-x-auto` box does not fail loudly on a phone — it fails QUIETLY. At 390px
// the org roster showed only NAME, with the access tier, the app count and the row's only action off
// to the right behind a scroll nothing announces; the activity record showed 2 of its 5 columns. So a
// reader had to choose between knowing who a row is about and knowing what it says, and could not
// reach the action at all. That is a shell that reflows correctly onto a page that cannot be operated.
//
// ══ WHAT `stackAt` DOES ════════════════════════════════════════════════════════════════════════
//
// Below the named breakpoint each row renders as a stacked BLOCK — one column's cell as its heading,
// the rest as quiet meta beneath — and the row may carry the action a button in a column used to.
// At and above it, nothing changes: the overrides are `max-*` variants, so the desktop table is
// untouched down to its class list, and a table that passes no `stackAt` is untouched at every width.
//
// ⚠ ONE DOM, NOT TWO. The alternative — render a table and a card list and hide one — duplicates
// every cell's testid, and `getAllByTestId`/Playwright counts double the moment both exist. The
// element switching is `display`, so the same cells serve both readings and nothing can drift.
//
// ⚠ THE ROW ACTION IS A REAL BUTTON. `onRowClick` makes a `<tr>` clickable and is mouse-only; using
// it here would take the action away from a keyboard or screen-reader user on the exact surface this
// mode exists to make operable. The chevron is a focusable control with its own accessible name, in a
// cell that does not exist at all above the breakpoint.

/** The widths a table may stack below. `sm` (40rem) is the estate's content breakpoint. */
export type DataTableStackBreakpoint = 'sm' | 'md' | 'lg';

/**
 * How a column renders inside a stacked row.
 *
 * `meta` shares a line with the metas beside it — right for two short facts (a badge and a count).
 * `line` takes a line of its own — right for a record whose facts are sentences, and the only way to
 * give every row of a repeating record the SAME anatomy. A wrapping row of shared metas gives short
 * content one shape, long content another and absent content a hole where its margin used to be:
 * three records, three geometries, which reads as a rendering fault rather than a hierarchy.
 */
export type DataTableStackedRole = 'heading' | 'meta' | 'line' | 'omit';

/**
 * The stacked-mode overrides, spelled out per breakpoint because Tailwind cannot see a computed
 * class name.
 *
 * The mechanism, in four lines: the table, its body and its rows become blocks; the header row goes
 * away (a stacked block reads its own labels); meta cells become `inline-block` so short facts share
 * a line and long ones wrap; and the action cell is positioned against the whole block rather than
 * flowing after the last fact. `pr-48` on the row reserves the action's lane (the chevron is a 32px
 * control inset 12px, so 48 clears it) so a long fact cannot run underneath it — and 48 is a REAL key
 * on the theme's scale: a value the scale does not carry silently falls back to Tailwind's 4px base,
 * which is how `pr-40` would have reserved 160px instead of 40 (caught by `pnpm check:spacing`). Every override names the SAME property the base utility sets (`px`/`py`, not `p`),
 * so the two cannot cascade in the wrong order.
 */
const STACK_CLASS: Record<
  DataTableStackBreakpoint,
  {
    wrapper: string;
    table: string;
    head: string;
    body: string;
    row: string;
    rowWithAction: string;
    heading: string;
    meta: string;
    line: string;
    label: string;
    omit: string;
    action: string;
    actionButton: string;
    fullWidthCell: string;
  }
> = {
  sm: {
    wrapper: 'max-sm:overflow-x-visible',
    table: 'max-sm:block',
    head: 'max-sm:hidden',
    body: 'max-sm:block',
    row: 'max-sm:relative max-sm:block max-sm:px-16 max-sm:py-12',
    rowWithAction: 'max-sm:pr-48',
    heading: 'max-sm:block max-sm:h-auto max-sm:max-w-none max-sm:px-0 max-sm:py-0',
    meta: 'max-sm:mr-10 max-sm:inline-block max-sm:h-auto max-sm:max-w-none max-sm:px-0 max-sm:py-0 max-sm:align-middle max-sm:text-s13 max-sm:empty:hidden',
    line: 'max-sm:block max-sm:h-auto max-sm:max-w-none max-sm:px-0 max-sm:py-0 max-sm:text-s13 max-sm:empty:hidden',
    label: 'hidden max-sm:inline',
    omit: 'max-sm:hidden',
    action:
      'hidden max-sm:absolute max-sm:inset-0 max-sm:block max-sm:h-auto max-sm:px-0 max-sm:py-0',
    actionButton:
      'max-sm:flex max-sm:h-full max-sm:w-full max-sm:items-center max-sm:justify-end max-sm:pr-12 max-sm:hover:bg-transparent',
    fullWidthCell: 'max-sm:block max-sm:h-auto max-sm:px-16 max-sm:py-0',
  },
  md: {
    wrapper: 'max-md:overflow-x-visible',
    table: 'max-md:block',
    head: 'max-md:hidden',
    body: 'max-md:block',
    row: 'max-md:relative max-md:block max-md:px-16 max-md:py-12',
    rowWithAction: 'max-md:pr-48',
    heading: 'max-md:block max-md:h-auto max-md:max-w-none max-md:px-0 max-md:py-0',
    meta: 'max-md:mr-10 max-md:inline-block max-md:h-auto max-md:max-w-none max-md:px-0 max-md:py-0 max-md:align-middle max-md:text-s13 max-md:empty:hidden',
    line: 'max-md:block max-md:h-auto max-md:max-w-none max-md:px-0 max-md:py-0 max-md:text-s13 max-md:empty:hidden',
    label: 'hidden max-md:inline',
    omit: 'max-md:hidden',
    action:
      'hidden max-md:absolute max-md:inset-0 max-md:block max-md:h-auto max-md:px-0 max-md:py-0',
    actionButton:
      'max-md:flex max-md:h-full max-md:w-full max-md:items-center max-md:justify-end max-md:pr-12 max-md:hover:bg-transparent',
    fullWidthCell: 'max-md:block max-md:h-auto max-md:px-16 max-md:py-0',
  },
  lg: {
    wrapper: 'max-lg:overflow-x-visible',
    table: 'max-lg:block',
    head: 'max-lg:hidden',
    body: 'max-lg:block',
    row: 'max-lg:relative max-lg:block max-lg:px-16 max-lg:py-12',
    rowWithAction: 'max-lg:pr-48',
    heading: 'max-lg:block max-lg:h-auto max-lg:max-w-none max-lg:px-0 max-lg:py-0',
    meta: 'max-lg:mr-10 max-lg:inline-block max-lg:h-auto max-lg:max-w-none max-lg:px-0 max-lg:py-0 max-lg:align-middle max-lg:text-s13 max-lg:empty:hidden',
    line: 'max-lg:block max-lg:h-auto max-lg:max-w-none max-lg:px-0 max-lg:py-0 max-lg:text-s13 max-lg:empty:hidden',
    label: 'hidden max-lg:inline',
    omit: 'max-lg:hidden',
    action:
      'hidden max-lg:absolute max-lg:inset-0 max-lg:block max-lg:h-auto max-lg:px-0 max-lg:py-0',
    actionButton:
      'max-lg:flex max-lg:h-full max-lg:w-full max-lg:items-center max-lg:justify-end max-lg:pr-12 max-lg:hover:bg-transparent',
    fullWidthCell: 'max-lg:block max-lg:h-auto max-lg:px-16 max-lg:py-0',
  },
};

/** No stacking configured: every slot is empty, so the rendered class lists are byte-identical. */
const NO_STACK: (typeof STACK_CLASS)['sm'] = {
  wrapper: '',
  table: '',
  head: '',
  body: '',
  row: '',
  rowWithAction: '',
  heading: '',
  meta: '',
  line: '',
  label: '',
  omit: '',
  action: '',
  actionButton: '',
  fullWidthCell: '',
};

/**
 * A column's role in a stacked row, defaulting the way a reader would: the FIRST column is what the
 * row is about, everything after it is detail.
 */
function stackedRole<Row>(column: DataTableColumn<Row>, index: number): DataTableStackedRole {
  return column.stacked ?? (index === 0 ? 'heading' : 'meta');
}

// ── Composed DataTable ──────────────────────────────────────────────────────────────────────────

export interface DataTableColumn<Row> {
  /** Stable column id (also the default sort key). */
  id: string;
  /** Header label. */
  header: ReactNode;
  /** Cell renderer for a row. */
  cell: (row: Row) => ReactNode;
  align?: CellAlign;
  numeric?: boolean;
  /** Mark sortable; the table reports clicks via `onSortChange`. */
  sortable?: boolean;
  /** Fixed/min width (CSS value, e.g. '160px' or '40%'). */
  width?: string;
  /** Header cell className (e.g. for a checkbox column). */
  headerClassName?: string;
  /** Clip overflowing cell content with an ellipsis (for emails / long text). */
  truncate?: boolean;
  /**
   * This column's place in a stacked row (see `stackAt`). Ignored entirely without it.
   *
   * Defaults: the first column is the `heading`, the rest are `meta`. `omit` drops a column from the
   * stacked reading — it is for a column whose control the row's own action REPLACES, never for a
   * fact, because a stacked block that silently withholds a column is the same failure as a table
   * scrolled off-screen.
   *
   * ⚠ A column rendering an interactive control must be `omit`ted when `onStackedRowAction` is
   * supplied — a control inside a block whose action is elsewhere is two targets in one place.
   */
  stacked?: DataTableStackedRole;
  /**
   * A word rendered before this cell's content in stacked mode ONLY, so a fact keeps the meaning its
   * column header carried.
   *
   * ⚠ THE HEADER ROW IS WHAT SAYS WHAT A CELL IS, and stacking throws it away. On a record whose
   * columns are two bare names — who a change happened TO and who DID it — the stack keeps both facts
   * and loses which is which. Rendered only when the cell has content, so a labelled empty column
   * still collapses.
   */
  stackedLabel?: string;
  /**
   * Extra classes merged onto this cell. Write them with the `max-*` prefix matching the table's
   * `stackAt`, so they apply to the stacked reading alone.
   *
   * ⚠ IT IS FOR SPACING AND TONE, not for layout roles — those are `stacked`. A record's provenance
   * line wants more air above it than its facts have between them, and only the consumer knows which
   * of its columns IS the provenance.
   */
  stackedClassName?: string;
}

export interface DataTableSort {
  columnId: string;
  direction: 'asc' | 'desc';
}

export interface DataTableProps<Row> extends TestIdProps {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  /** Stable key per row. */
  rowKey: (row: Row) => string;
  /** Optional per-row testid. */
  rowTestId?: (row: Row) => string;
  /** Row click handler (also flips rows to the interactive hover style). */
  onRowClick?: (row: Row) => void;
  /** Toolbar content rendered in a sticky bar above the table (search/filters/actions). */
  toolbar?: ReactNode;
  /** Controlled sort state. */
  sort?: DataTableSort | null;
  onSortChange?: (sort: DataTableSort) => void;
  /** Loading → renders shimmer rows. */
  loading?: boolean;
  /** Number of skeleton rows while loading (default 5). */
  loadingRows?: number;
  /**
   * What a screen reader HEARS while `loading` is true. Defaults to "Loading…".
   *
   * ⚠ A STATE THAT IS ONLY SHOWN IS NOT A STATE THAT WAS DELIVERED. Skeleton rows are `aria-hidden`
   * by construction, so before this the loading and error transitions of every table in the estate
   * reached sighted users alone. Name the subject ("Loading your team…") — a table on a page with
   * two of them is otherwise announced twice, identically.
   */
  loadingLabel?: string;
  /** Optional testId for the loading region (preserve a screen's `*-loading` e2e selector). */
  loadingTestId?: string;
  /** Error message → renders an inline error row with an optional retry. */
  error?: ReactNode;
  onRetry?: () => void;
  /** Optional testIds for the error region + the retry button (preserve `*-error`/`*-error-retry`). */
  errorTestId?: string;
  retryTestId?: string;
  /** Empty slot (an <EmptyState/>) when rows.length === 0 and not loading/error. */
  emptyState?: ReactNode;
  /** Pagination footer content (counts + prev/next). Use <TablePagination/> for the standard one. */
  footer?: ReactNode;
  /** Pin the header row while the body scrolls. */
  stickyHeader?: boolean;
  /** Row height. 'comfortable' gives rich multi-line cells (avatar + name + tag) room to breathe. */
  density?: 'default' | 'comfortable';
  /**
   * Disclosure content for a row. Providing it adds a leading toggle column. Return `null` for a row
   * that has nothing to disclose — that row gets no toggle (and cannot be expanded).
   */
  renderExpanded?: (row: Row) => ReactNode;
  /** Controlled expanded row keys. Omit to let the table hold the state itself. */
  expandedKeys?: string[];
  onExpandedChange?: (keys: string[]) => void;
  /** Accessible label for a row's disclosure toggle. Receives the row so it can name the subject. */
  expandLabel?: (row: Row) => string;
  /**
   * Override the disclosure toggle's testid. Defaults to `${rowTestId(row)}-expand`, which is right
   * for most tables — but a screen whose rows are selected by a `[data-testid^="<rowPrefix>"]`
   * locator must NOT let the toggle share that prefix or the locator counts every row twice. Such a
   * screen passes an id outside its row namespace.
   */
  expandTestId?: (row: Row) => string;
  /**
   * Render each row as a stacked block BELOW this breakpoint. Omitted = today's table at every width.
   *
   * See the STACK_CLASS block above for what it does and the defect it cures. Per-column placement is
   * `DataTableColumn.stacked`; the row's action is `onStackedRowAction`.
   */
  stackAt?: DataTableStackBreakpoint;
  /**
   * The row's action while STACKED — a chevron button at the row's right, replacing the control an
   * `omit`ted column carried. Stacked-only by design: `onRowClick` would make the desktop row
   * clickable too, which is a change to the table this mode promises not to touch.
   */
  onStackedRowAction?: (row: Row) => void;
  /** Accessible name for that action. Receives the row so it can name its subject. */
  stackedRowActionLabel?: (row: Row) => string;
  /** Override the action's testid. Defaults to `${rowTestId(row)}-open`. */
  stackedRowActionTestId?: (row: Row) => string;
  /**
   * Withhold the row's action on rows this answers `false` for. A control the caller may not perform
   * (one the server would refuse) is not drawn — no button, no action cell, no reserved edge — so the
   * row reads exactly as a row of a table with no action does, while its neighbours keep theirs.
   * Omitted = every row acts. The table reading is untouched either way: a column decides its own cell.
   */
  stackedRowActionWhen?: (row: Row) => boolean;
  className?: string;
}

// ── Row ─────────────────────────────────────────────────────────────────────────────────────────
//
// Split out of the body map and memoized so a parent state change that leaves a row's inputs alone
// (opening a dialog, typing in the toolbar search) does not re-render that row's cells. On a 201-row
// table the row subtree — the design-system components a `cell` returns — is ~90% of a re-render, so
// this is the only boundary where the cost can be avoided rather than made cheaper.
//
// Everything derived from a caller function is resolved to a STRING or BOOLEAN in the parent (the
// key, the testids, the expand label, the open state) so the default shallow compare succeeds:
// `rowTestId={(r) => `row-${r.id}`}` is a new closure every render but yields the same string, and a
// string compares by value. Interaction callbacks are ref-latched by the parent, so they too hold a
// stable identity while still calling the caller's newest closure.
//
// `columns` and `renderExpanded` are deliberately NOT stabilized: their functions run during render
// and legitimately close over caller state (a selection Set, a pending-mutation id), so honoring
// their identity is what keeps a memoized row correct. A caller that rebuilds `columns` inline every
// render therefore re-renders its rows — correctly, and exactly as it does today.
interface DataTableRowProps<Row> {
  row: Row;
  rowId: string;
  columns: DataTableColumn<Row>[];
  density: 'default' | 'comfortable';
  expandable: boolean;
  colCount: number;
  detailId: string;
  isExpanded: boolean;
  interactive: boolean;
  rowTestId?: string;
  expandTestId?: string;
  expandLabel?: string;
  /** The resolved stacked-mode class slots (every slot empty when the table does not stack). */
  stack: (typeof STACK_CLASS)['sm'];
  stackedActionLabel?: string;
  stackedActionTestId?: string;
  renderExpanded?: (row: Row) => ReactNode;
  onRowClick?: (row: Row) => void;
  onStackedRowAction?: (row: Row) => void;
  onToggleExpanded?: (key: string) => void;
}

function DataTableRowInner<Row>({
  row,
  rowId,
  columns,
  density,
  expandable,
  colCount,
  detailId,
  isExpanded,
  interactive,
  rowTestId,
  expandTestId,
  expandLabel,
  stack,
  stackedActionLabel,
  stackedActionTestId,
  renderExpanded,
  onRowClick,
  onStackedRowAction,
  onToggleExpanded,
}: DataTableRowProps<Row>) {
  const detail = renderExpanded?.(row) ?? null;
  const isOpen = detail != null && isExpanded;
  const stackedAction = onStackedRowAction != null;

  return (
    <>
      <TR
        interactive={interactive}
        onClick={interactive ? () => onRowClick?.(row) : undefined}
        testId={rowTestId}
        // `group` lets row cells reveal hover-only affordances (e.g. a row action button).
        className={cn('group', stack.row, stackedAction && stack.rowWithAction)}
      >
        {expandable && (
          // The disclosure toggle is a table affordance; a stacked block has no columns to disclose
          // BESIDE, so it stands down with the header row rather than floating over the block.
          <TD className={cn('w-[44px] px-0 text-center', stack.omit)}>
            {detail != null && (
              <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={detailId}
                aria-label={expandLabel ?? 'Show details'}
                // A clickable row must not also fire when the toggle is what was clicked.
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpanded?.(rowId);
                }}
                className={cn(
                  'inline-flex size-24 items-center justify-center rounded-xs',
                  'text-icon-muted outline-none transition-colors',
                  'hover:bg-neutral-100 hover:text-text-strong focus-visible:shadow-ring',
                )}
                {...testIdProp(expandTestId)}
              >
                <Icon icon={isOpen ? ChevronDown : ChevronRight} size={16} aria-hidden />
              </button>
            )}
          </TD>
        )}
        {columns.map((col, index) => {
          const content = col.cell(row);
          // The label rides the CONTENT, so a column that renders nothing for this row collapses
          // whole rather than leaving a bare word behind.
          const labelled =
            col.stackedLabel !== undefined && content !== null && content !== undefined;
          return (
            <TD
              key={col.id}
              align={col.align}
              numeric={col.numeric}
              // `max-w-0` lets the cell shrink so the inner `truncate` can clip with an ellipsis.
              className={cn(
                density === 'comfortable' && 'h-[68px]',
                col.truncate && 'max-w-0',
                stack[stackedRole(col, index)],
                col.stackedClassName,
              )}
            >
              {labelled && <span className={stack.label}>{col.stackedLabel} </span>}
              {col.truncate ? <span className="block truncate">{content}</span> : content}
            </TD>
          );
        })}
        {stackedAction && (
          // ⚠ THE ROW IS THE ACTION, and this is how it is one control rather than two.
          //
          // The cell does not exist above the breakpoint — `display: none` keeps it out of table
          // layout entirely, so the row never grows a column the header does not have. BELOW it, the
          // cell becomes an `inset-0` overlay of the whole record and the button fills it, with the
          // chevron parked at the right edge. A ~343x90px block carrying a right-edge chevron is the
          // strongest "tap anywhere on this row" signal a phone has, and a 32px live target inside it
          // is a promise the surface does not keep — a tap on the name would do nothing, which is the
          // very "cannot reach the action" failure the stacked mode exists to cure, behind a smaller
          // door. Stretching the hit area keeps ONE focusable, one accessible name and no nested
          // interactive, and puts the 44px touch floor beyond argument by construction.
          //
          // ⚠ The overlay stays TRANSPARENT below the breakpoint. It paints above the record's own
          // cells, so any hover fill would cover the text it sits on; hover is a pointer affordance
          // and this mode is the phone reading. The focus outline, drawn around the whole block, is
          // the state that matters here and is better for it.
          <TD className={stack.action}>
            <button
              type="button"
              aria-label={stackedActionLabel ?? 'Open'}
              onClick={(e) => {
                e.stopPropagation();
                onStackedRowAction?.(row);
              }}
              className={cn(
                'inline-flex size-32 items-center justify-center rounded-md',
                // `text-icon`, never `icon-muted`: at 2.52:1 that grey is the system's sanctioned
                // DISABLED foreground, and this glyph is the row's only visible affordance.
                'text-icon outline-none transition-colors',
                'hover:bg-neutral-100 hover:text-text-strong',
                FOCUS_RING,
                stack.actionButton,
              )}
              {...testIdProp(stackedActionTestId)}
            >
              <Icon icon={ChevronRight} size={18} aria-hidden />
            </button>
          </TD>
        )}
      </TR>
      {isOpen && (
        <TR id={detailId} className={cn('bg-surface-sunken', stack.row)}>
          <td colSpan={colCount} className={cn('px-16 py-12', stack.fullWidthCell)}>
            {detail}
          </td>
        </TR>
      )}
    </>
  );
}

// `memo` erases the generic, so the cast restores it — the component is otherwise unchanged.
const DataTableRow = memo(DataTableRowInner) as typeof DataTableRowInner;

/**
 * Hold the newest callback behind a stable identity. The wrapper is created once, so it never busts a
 * memoized child, and it always dispatches to the caller's latest closure. Safe here because every
 * latched callback fires from an event, never during render.
 */
function useLatestCallback<Args extends unknown[], R>(
  fn: ((...args: Args) => R) | undefined,
): (...args: Args) => R | undefined {
  const ref = useRef(fn);
  ref.current = fn;
  return useCallback((...args: Args) => ref.current?.(...args), []);
}

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  rowTestId,
  onRowClick,
  toolbar,
  sort,
  onSortChange,
  loading = false,
  loadingRows = 5,
  loadingLabel = 'Loading…',
  loadingTestId,
  errorTestId,
  retryTestId,
  error,
  onRetry,
  emptyState,
  footer,
  stickyHeader = false,
  density = 'default',
  renderExpanded,
  expandedKeys,
  onExpandedChange,
  expandLabel,
  expandTestId,
  stackAt,
  onStackedRowAction,
  stackedRowActionLabel,
  stackedRowActionTestId,
  stackedRowActionWhen,
  className,
  testId,
}: DataTableProps<Row>) {
  const expandable = renderExpanded != null;
  // Every slot is the empty string without `stackAt`, so a table that does not stack renders exactly
  // the class lists it rendered before this mode existed.
  const stack = stackAt === undefined ? NO_STACK : STACK_CLASS[stackAt];
  // Uncontrolled fallback: the hook always runs (never conditionally), it is simply ignored when the
  // caller controls `expandedKeys`.
  const [internalExpanded, setInternalExpanded] = useState<string[]>([]);
  const expanded = expandedKeys ?? internalExpanded;

  const toggleExpanded = (key: string) => {
    const next = expanded.includes(key) ? expanded.filter((k) => k !== key) : [...expanded, key];
    if (expandedKeys == null) setInternalExpanded(next);
    onExpandedChange?.(next);
  };

  // Stable identities for the callbacks a row holds, so a caller's inline arrow does not bust the
  // row memo. All fire from a click, so latching cannot serve a stale render value.
  const handleRowClick = useLatestCallback(onRowClick);
  const handleToggleExpanded = useLatestCallback(toggleExpanded);
  const latchedStackedAction = useLatestCallback(onStackedRowAction);
  // ⚠ Latched only when the caller supplied one: the latch is always defined, so passing it
  // unconditionally would make every row believe it has an action and grow a chevron.
  const handleStackedRowAction = onStackedRowAction == null ? undefined : latchedStackedAction;

  // The disclosure column is a real column for colSpan purposes — the error/empty rows must span it too.
  const colCount = columns.length + (expandable ? 1 : 0);

  const headerSortState = (col: DataTableColumn<Row>): 'asc' | 'desc' | false | undefined => {
    if (!col.sortable) return undefined;
    if (sort?.columnId === col.id) return sort.direction;
    return false;
  };

  const handleSort = (col: DataTableColumn<Row>) => {
    if (!col.sortable || !onSortChange) return;
    const next: DataTableSort['direction'] =
      sort?.columnId === col.id && sort.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ columnId: col.id, direction: next });
  };

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-border-subtle bg-surface shadow-xs',
        className,
      )}
      // The `testId` marks the LOADED list. While `loading`, the table shows skeleton rows (with the
      // wrapper present visually) but withholds the `testId` so a `findByTestId(list)` + sync
      // `getAllByTestId(row)` pattern resolves only once the rows exist (the screen list contract).
      {...testIdProp(loading ? undefined : testId)}
    >
      {/* ⚠ THE LIVE REGION IS ALWAYS MOUNTED, and only its TEXT changes. A `role="status"` inserted
          at the moment it has something to say is frequently missed — the assistive technology has
          to observe the region before it can observe a change to it. Polite, because a table filling
          in is not an interruption; the error below is `role="alert"`, because a failed read is. */}
      <span className="sr-only" role="status" aria-live="polite">
        {loading ? loadingLabel : ''}
      </span>
      {toolbar != null && (
        <div className="flex flex-wrap items-center justify-between gap-12 border-b border-border-subtle px-16 py-12">
          {toolbar}
        </div>
      )}
      <Table className={stack.table} wrapperClassName={stack.wrapper}>
        {/* The header row is the one thing a stacked block genuinely replaces: it labels COLUMNS, and
            a block has none. Hiding it also takes it out of the a11y tree, which is right — a screen
            reader announcing five column names over a stacked list would be describing a table that
            is not being rendered. */}
        <THead sticky={stickyHeader} className={stack.head}>
          <tr>
            {expandable && (
              // Header for the disclosure column: no visible label (the per-row buttons carry their
              // own names), so it is hidden from the a11y tree rather than announced as a blank column.
              <TH aria-hidden className="w-[44px] px-0" style={{ width: '44px' }} />
            )}
            {columns.map((col) => (
              <TH
                key={col.id}
                align={col.align}
                sort={headerSortState(col)}
                onSort={() => handleSort(col)}
                style={col.width ? { width: col.width } : undefined}
                className={col.headerClassName}
              >
                {col.header}
              </TH>
            ))}
          </tr>
        </THead>
        <TBody className={stack.body}>
          {loading ? (
            Array.from({ length: loadingRows }).map((_, r) => (
              <TR
                key={`sk-${r}`}
                className={stack.row}
                {...(r === 0 ? testIdProp(loadingTestId) : {})}
              >
                {expandable && <TD className={cn('w-[44px] px-0', stack.omit)} />}
                {columns.map((col, index) => (
                  <TD key={col.id} align={col.align} className={stack[stackedRole(col, index)]}>
                    <Skeleton
                      className={cn('h-12', col.align === 'right' ? 'ml-auto w-16' : 'w-3/4')}
                    />
                  </TD>
                ))}
              </TR>
            ))
          ) : error != null ? (
            <tr className={stack.row}>
              <td
                colSpan={colCount}
                className={cn('px-16 py-32', stack.fullWidthCell)}
                {...testIdProp(errorTestId)}
              >
                {/* ⚠ THE ROLE IS ON THIS DIV, NOT ON THE `<td>`. ARIA in HTML allows a `<td>` inside a
                    `role=table` table exactly one role — `cell` — so `role="alert"` there is an
                    invalid override that replaces the cell semantic rather than adding to it. The
                    cell keeps being a cell; its content is the live region. Assertive, because a
                    read that failed is the one table state a person must not scroll past. */}
                <div role="alert" className="flex flex-col items-center gap-8 text-center">
                  <p className="font-sans text-s14 font-w500 tracking-givmo text-danger-700">
                    {error}
                  </p>
                  {onRetry != null && (
                    <button
                      type="button"
                      onClick={onRetry}
                      className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:underline focus-visible:underline"
                      {...testIdProp(retryTestId)}
                    >
                      Try again
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr className={stack.row}>
              <td colSpan={colCount} className={stack.fullWidthCell}>
                {emptyState}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const key = rowKey(row);
              const testIdForRow = rowTestId?.(row);
              return (
                <DataTableRow
                  key={key}
                  row={row}
                  rowId={key}
                  columns={columns}
                  density={density}
                  expandable={expandable}
                  colCount={colCount}
                  // Scoped to the table so two tables on one page cannot collide on the same row key.
                  detailId={`${testId ?? 'data-table'}-detail-${key}`}
                  isExpanded={expanded.includes(key)}
                  interactive={onRowClick != null}
                  rowTestId={testIdForRow}
                  expandTestId={
                    expandTestId
                      ? expandTestId(row)
                      : testIdForRow
                        ? `${testIdForRow}-expand`
                        : undefined
                  }
                  expandLabel={expandLabel?.(row)}
                  stack={stack}
                  stackedActionLabel={stackedRowActionLabel?.(row)}
                  stackedActionTestId={
                    stackedRowActionTestId
                      ? stackedRowActionTestId(row)
                      : testIdForRow
                        ? `${testIdForRow}-open`
                        : undefined
                  }
                  renderExpanded={renderExpanded}
                  onRowClick={handleRowClick}
                  onStackedRowAction={
                    stackedRowActionWhen === undefined || stackedRowActionWhen(row)
                      ? handleStackedRowAction
                      : undefined
                  }
                  onToggleExpanded={handleToggleExpanded}
                />
              );
            })
          )}
        </TBody>
      </Table>
      {footer != null && (
        <div className="flex items-center justify-between gap-12 border-t border-border-subtle px-16 py-12">
          {footer}
        </div>
      )}
    </div>
  );
}

// ── TablePagination — the standard footer ─────────────────────────────────────────────────────

export interface TablePaginationProps extends TestIdProps {
  /** Total number of rows across all pages. */
  total: number;
  /** Rows per page. */
  pageSize: number;
  /** 1-based current page. */
  page: number;
  onPrev: () => void;
  onNext: () => void;
  /** When true, render even if total <= pageSize (default false — single-page lists hide it). */
  alwaysShow?: boolean;
  /** Label for the unit being counted (default "results"). */
  noun?: string;
}

/**
 * Standard pagination footer: "{start}–{end} of {total}" + Prev/Next with disabled boundary states
 * (Prev off on page 1, Next off on the last page). Returns null when there's a single page unless
 * `alwaysShow`. Render inside `<DataTable footer={…}>`.
 */
export function TablePagination({
  total,
  pageSize,
  page,
  onPrev,
  onNext,
  alwaysShow = false,
  noun = 'results',
  testId,
}: TablePaginationProps) {
  if (!alwaysShow && total <= pageSize) return null;
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const isFirst = page <= 1;
  const isLast = end >= total;

  return (
    <>
      <span
        className="font-sans text-s13 tracking-normal text-text-tertiary tabular-nums"
        {...testIdProp(testId ? `${testId}-count` : undefined)}
      >
        {start}–{end} of {total} {noun}
      </span>
      <div className="flex items-center gap-6">
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          disabled={isFirst}
          onClick={onPrev}
          testId={testId ? `${testId}-prev` : undefined}
        >
          Previous
        </Button>
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          disabled={isLast}
          onClick={onNext}
          testId={testId ? `${testId}-next` : undefined}
        >
          Next
        </Button>
      </div>
    </>
  );
}
